import type { ConfigNode, ConfigObjectMetadata } from '../types/config.types'
import type { FormSchema } from '../types/form.types'
import { getMockMetadata } from '../mocks/metadata.mock'
import apiClient, { ApiService, TokenManager } from '@/utils/api'
import { resolveEntityMetadataTableKey, resolveEntityMetadataObjectName } from '../utils/entityMetadataKey'
// Deep import, not the '@/report-builder' barrel: the barrel re-exports
// WorkbenchShell, which drags recharts into the entry chunk. types.ts is a
// zero-import leaf.
import { emptyReportDefinition } from '@/report-builder/types'

const delay = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms))

function normalizeDesignerTreeReportNodes(nodes: ConfigNode[]): ConfigNode[] {
  return nodes.map((node) => {
    const nextType =
      typeof node.type === 'string' && node.type.toLowerCase() === 'report'
        ? ('report' as const)
        : node.type
    let next: ConfigNode = nextType !== node.type ? { ...node, type: nextType } : { ...node }
    // Heal legacy nodes saved without a definition so App nav can list them.
    if (next.type === 'report' && (!next.report || typeof next.report !== 'object')) {
      next = { ...next, report: emptyReportDefinition(next.name || 'Report') }
    }
    if (next.children?.length) {
      next.children = normalizeDesignerTreeReportNodes(next.children)
    }
    return next
  })
}

export function normalizeDesignerTree(nodes: ConfigNode[]): ConfigNode[] {
  return normalizeDesignerTreeFormSchemas(normalizeDesignerTreeReportNodes(nodes))
}

function normalizeDesignerTreeFormSchemas(nodes: ConfigNode[]): ConfigNode[] {
  return nodes.map((node) => {
    const snake = (node as ConfigNode & { form_schema?: FormSchema }).form_schema
    const next: ConfigNode = {
      ...node,
      ...(snake && !node.formSchema ? { formSchema: snake } : {}),
    }
    if (next.children?.length) {
      next.children = normalizeDesignerTreeFormSchemas(next.children)
    }
    return next
  })
}

/** A `theme` node without a `themeData` payload is an auto-injected builtin placeholder. */
function isPlaceholderThemeNode(node: ConfigNode): boolean {
  return (
    node.type === 'theme' &&
    (!node.themeData || Object.keys(node.themeData).length === 0)
  )
}

/** Drop auto-injected builtin theme placeholders; user themes (with themeData) stay. */
export function stripBuiltinThemesFromTree(nodes: ConfigNode[]): ConfigNode[] {
  return nodes.map((node) => {
    if (node.type === 'themes') {
      return {
        ...node,
        children: (node.children ?? []).filter((c) => !isPlaceholderThemeNode(c)),
      }
    }
    if (node.children?.length) {
      return { ...node, children: stripBuiltinThemesFromTree(node.children) }
    }
    return node
  })
}

export const configService = {
  /** Load tree from the selected connection. Failures surface as errors — no synthetic tree. */
  getTree: async (connectionId: string | null): Promise<ConfigNode[]> => {
    if (!connectionId || !TokenManager.getAccessToken()) {
      throw new Error('No connection selected')
    }
    const res = await ApiService.get<{ tree: ConfigNode[] | null }>(
      `/v1/designer/config-tree?connection_id=${encodeURIComponent(connectionId)}`
    )
    if (!res?.tree || !Array.isArray(res.tree) || res.tree.length === 0) {
      throw new Error('Configuration tree is empty')
    }
    return stripBuiltinThemesFromTree(normalizeDesignerTree(res.tree))
  },

  saveTree: async (
    connectionId: string,
    tree: ConfigNode[]
  ): Promise<{
    ok?: boolean
    sync_results?: {
      created_tables?: string[]
      errors?: string[]
      // config_migration.errors carries per-object validation errors from
      // the tree->_config_objects sync pass (e.g. branch-common collisions)
      // that never land in the top-level errors array.
      config_migration?: { errors?: string[] }
    }
  }> => {
    return ApiService.put('/v1/designer/config-tree', {
      connection_id: connectionId,
      tree,
    })
  },

  getMetadata: async (
    nodeId: string,
    connectionId?: string | null,
    tree?: ConfigNode[] | null
  ): Promise<ConfigObjectMetadata | null> => {
    if (!connectionId) {
      await delay(30)
      return getMockMetadata(nodeId)
    }

    const tableKey = resolveEntityMetadataTableKey(nodeId, tree ?? undefined)
    if (!tableKey) return null // not an entity node — don't hit API

    const objectName = resolveEntityMetadataObjectName(nodeId, tree ?? undefined)
    const metaQs = new URLSearchParams({ connection_id: String(connectionId) })
    if (objectName) metaQs.set('object_name', objectName)

    try {
      // Use apiClient directly with validateStatus to accept 404 silently
      // (404 is expected when form is opened for a node without a backing entity)
      const response = await apiClient.get<any>(
        `/v1/designer/entity/${encodeURIComponent(tableKey)}/metadata?${metaQs.toString()}`,
        { validateStatus: (status) => status === 200 || status === 404 }
      )
      if (response.status === 404) return null
      const res = response.data
      if (!res) return null

      const attributes = (res.attributes ?? []).map((a: any) => {
        const name = a?.name ?? ''
        return {
          id: a?.id ?? name,
          name,
          synonym: a?.synonym ?? a?.synonyms ?? undefined,
          type: a?.type ?? 'string',
          length: a?.length ?? undefined,
          precision: undefined,
          indexed: false,
          required: a?.nullable === false,
        }
      })

      const tabularSections = (res.table_parts ?? []).map((tp: any) => ({
        id: tp?.table_name ?? tp?.name ?? nodeId,
        name: tp?.name ?? 'TablePart',
        attributes: (tp?.columns ?? []).map((c: any) => ({
          id: c?.id ?? c?.name ?? '',
          name: c?.name ?? '',
          synonym: c?.synonyms ?? undefined,
          type: c?.type ?? 'string',
          length: c?.length ?? undefined,
          precision: undefined,
          indexed: false,
          required: c?.nullable === false,
        })),
      }))

      return {
        id: res.table_name ?? nodeId,
        type: res.type ?? 'catalog',
        name: res.name ?? nodeId,
        synonym: res.synonyms,
        comment: undefined,
        attributes,
        tabularSections,
        forms: [],
        templates: [],
      }
    } catch {
      await delay(30)
      return getMockMetadata(nodeId)
    }
  },

  // TODO: replace with API call → PUT /api/config/metadata/:nodeId
  updateMetadata: async (_nodeId: string, _metadata: ConfigObjectMetadata): Promise<void> => {
    await delay(100)
  },
}
